import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Brain, Lightbulb, Layers, Cpu, Zap } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { SEO } from '@/components/SEO';

const workflows = [
  { icon: Brain, title: 'AI-Assisted Development', desc: 'Using AI tools for script analysis, scene breakdowns, and creative development — accelerating the path from concept to treatment.' },
  { icon: Lightbulb, title: 'Visual Pre-Production', desc: 'AI-generated mood boards, location references, and visual explorations that help align creative vision before a single frame is shot.' },
  { icon: Layers, title: 'Hybrid Production Pipelines', desc: 'Blending traditional filmmaking craft with AI-enhanced workflows for scheduling, shot planning, and on-set efficiency.' },
  { icon: Cpu, title: 'Smart Dailies & QC', desc: 'AI-powered quality control and automated metadata tagging that streamline the review process without replacing human judgment.' },
  { icon: Zap, title: 'Post-Production Enhancement', desc: 'AI tools for noise reduction, upscaling, and workflow acceleration — always under the supervision of experienced colorists and editors.' },
  { icon: Sparkles, title: 'Innovation Lab', desc: 'Ongoing exploration of emerging tools and techniques. We test, evaluate, and adopt only what genuinely serves the story.' },
];

export default function InnovationPage() {
  return (
    <div>
      <SEO 
        title="AI-Enhanced Film Production Workflows | Baklight Innovation Lab"
        description="Human-led AI workflows for film production. Smart pre-production, automated dailies, and post-production enhancement — always under creative human supervision."
        path="/innovation"
      />
      <PageHeader
        meta="Innovation / AI Workflows"
        title="Future-facing. Human-led."
        subtitle="At Baklight, we embrace AI as a creative tool — not a replacement. Our approach integrates new technology thoughtfully into production pipelines, always in service of story."
      />

      {/* Philosophy */}
      <section className="pb-14 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="max-w-[700px]">
              <p className="font-display text-xl md:text-2xl text-[hsl(var(--bk-fg))] leading-snug">
                "Technology should amplify human creativity, not replace it. We use AI where it genuinely improves the process — and step back where craft demands a human hand."
              </p>
              <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mt-4">Paco Herrera</p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Workflows Grid */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">AI-Enhanced Workflows</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-12">
              Where we apply innovation.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflows.map((item, i) => (
              <SectionReveal key={item.title} delay={i * 0.08}>
                <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg))] h-full">
                  <item.icon size={24} className="text-[hsl(var(--bk-accent))] mb-4" strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-14 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Our Principles</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-10">
              Guardrails for innovation.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Story First', desc: 'Every tool we adopt must serve the story. If it doesn\'t improve the narrative, we don\'t use it.' },
              { num: '02', title: 'Human Oversight', desc: 'AI assists. Humans decide. Every creative decision is made by experienced filmmakers.' },
              { num: '03', title: 'Selective Adoption', desc: 'We test rigorously before adopting. Not every new tool deserves a place in our pipeline.' },
            ].map((item, i) => (
              <SectionReveal key={item.num} delay={i * 0.1}>
                <div>
                  <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">{item.num}</p>
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Projects */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Projects</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-10">
              Innovation in Practice
            </h2>
          </SectionReveal>

          {/* Project Grid - Using WorkPage pattern */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {[
              {
                slug: 'san-esteban',
                title: 'San Esteban: Freedom, Mysticism and Nopales',
                category: 'Documentary',
                year: '2025',
                thumbnail: 'https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/posters/003/639/002/original/c93512a45a-poster.jpg?1772234101',
                note: 'AI-enhanced workflow planning'
              },
              {
                slug: 'el-batan',
                title: 'El Batán, Small and Pretty',
                category: 'Documentary',
                year: '2024',
                thumbnail: 'https://filmfreeway-production-storage-01-connector.filmfreeway.com/press_kits/posters/002/873/095/original/bb12286a4b-poster.jpg?1729710817',
                note: 'Smart dailies & automated QC'
              },
              {
                slug: 'patron-mexican-heritage',
                title: 'Patrón — Mexican Heritage',
                category: 'Commercial',
                year: '2023',
                thumbnail: '/assets/carousel/Mexican Heritage Tin.jpg',
                note: 'Hybrid production pipeline'
              }
            ].map((project, i) => (
              <SectionReveal key={project.slug} delay={i * 0.05}>
                <Link 
                  to={`/work/${project.slug}`} 
                  className="group block"
                  data-testid={`innovation-project-${project.slug}`}
                >
                  <div className="relative aspect-[2/3] rounded-md overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-2">
                    <img 
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase mb-1">{project.category} · {project.year}</p>
                  <h3 className="font-display text-sm text-[hsl(var(--bk-fg))] tracking-tight line-clamp-2 mb-1">{project.title}</h3>
                  <p className="font-body text-xs text-[hsl(var(--bk-accent))]/80 italic">{project.note}</p>
                </Link>
              </SectionReveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
            >
              View All Work <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
              Curious about our approach?
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-4 max-w-[500px] mx-auto">
              We're happy to discuss how AI-enhanced workflows can benefit your next production.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98] mt-8"
            >
              Let's Talk Innovation <ArrowRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
